<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'PaymentsFilters',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'submit']);

  const props = defineProps({});

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const createAccountGrups = (...args) => {
    const [accounts] = args;
    const showInactiveAccounts = item.value.showInactiveAccounts;

    return _.chain(accounts)
      .groupBy((p) => p.owner.id)
      .map((items, owner_id) => ({
        id: owner_id,
        name: items[0].owner.name,
        items: !showInactiveAccounts
          ? _.filter(items, { inactive: false })
          : items,
      }))
      .filter((p) => p.items.length)
      .value();
  };

  const item = ref({
    date_from: null,
    date_to: null,
    account: null,
    showInactiveAccounts: false,
  });
  const accounts = ref(null);
  const accountGroups = ref(null);
  const ux = ref([]);
  const b_5962862420439144_style = computed(() => ({
    width: '500px',
  }));

  const $data = computed(() => ({
    item: item.value,
    accounts: accounts.value,
    accountGroups: accountGroups.value,
    ux: ux.value,
    b_5962862420439144_style: b_5962862420439144_style.value,
  }));

  const $$load_accounts = async () => {
    return $fn.value
      .fetch('list_account')
      .then((resp) =>
        _.orderBy(
          resp.data,
          ['inactive', 'account_type', 'name'],
          ['asc', 'desc', 'asc']
        )
      );
  };
  const $$load_accountGroups = async () => {
    return createAccountGrups($data.value.accounts);
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item', cache: { type: 'local', key: 'PaymentsFilters' } },
      { name: 'accounts' },
      { name: 'accountGroups', dependsOn: ['accounts'] },
      { name: 'ux' },
      { name: 'b_5962862420439144_style', nodeId: '5962862420439144' },
    ],
    loaders: { accounts: $$load_accounts, accountGroups: $$load_accountGroups },
    ux: {},
    dataRefs: { item, accounts, accountGroups, ux, b_5962862420439144_style },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const selectedAccountType = computed(() => {
    if (loaded.value) {
      return item.value.account && item.value.account.account_type;
    }
    return null;
  });
  const selectedAccount = computed(() => {
    if (loaded.value) {
      return _.isObject(item.value.account) ? item.value.account : null;
    }
    return null;
  });

  const act_2248226175642056_emit_ud1m6b67a_getActionArgs = async ($event) => {
    return {
      event: 'submit',
      value: { ...item.value, account: item.value.account?.id },
    };
  };
  const inst_evh_2248226175642056_input_sk80exgs3_getWhen = async ($event) => {
    return !!item.value.date_from;
  };
  const act_5962862420439144_setData_48sk0i800_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.date_from',
      value: $event.data[0],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_5962862420439144_setData_76xz73xu6_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.date_to',
      value: $event.data[1],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_8358196963355853_emit_79utfffhm_getActionArgs = async ($event) => {
    return {
      event: 'submit',
      value: { ...item.value, account: item.value.account?.id },
    };
  };
  const act_6424600534600827_showModal_u9w69quwz_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        accountId: item.value.account ? item.value.account.id : null,
        transactionKind: 'cash_book',
        transactionType: 'deposit',
      },
    };
  };
  const act_8249915319319009_showModal_gzp8va95p_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        accountId: item.value.account ? item.value.account.id : null,
        transactionKind: 'cash_book',
        transactionType: 'payment',
      },
    };
  };
  const act_2276125547171722_showModal_evby1d8gp_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        accountId: item.value.account ? item.value.account.id : null,
        transactionKind:
          selectedAccountType.value === 'bank_account'
            ? 'bank'
            : selectedAccountType.value,
      },
    };
  };
  const act_1836715606397834_showModal_001nso7c8_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ImportBankStatement',
    };
  };

  const act_2248226175642056_emit_ud1m6b67a = new actionTypes.EmitAction({
    actionArgs: act_2248226175642056_emit_ud1m6b67a_getActionArgs,
    displayName: 'load',
  });
  const inst_evh_2248226175642056_input_sk80exgs3 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_emit_ud1m6b67a,
      event: 'input',
      when: inst_evh_2248226175642056_input_sk80exgs3_getWhen,
      displayName: 'load',
    });
  const inst_evh_2248226175642056_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_input_sk80exgs3],
    event: 'input',
  });
  const act_5962862420439144_setData_48sk0i800 = new actionTypes.SetDataAction({
    actionArgs: act_5962862420439144_setData_48sk0i800_getActionArgs,
  });
  const inst_evh_5962862420439144_changed_kk8dqb8ai =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_setData_48sk0i800,
      event: 'changed',
      displayName: 'setData',
    });
  const act_5962862420439144_setData_76xz73xu6 = new actionTypes.SetDataAction({
    actionArgs: act_5962862420439144_setData_76xz73xu6_getActionArgs,
  });
  const inst_evh_5962862420439144_changed_cgtftdbe7 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_setData_76xz73xu6,
      event: 'changed',
      displayName: 'setData',
    });
  const inst_evh_5962862420439144_changed = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_5962862420439144_changed_kk8dqb8ai,
      inst_evh_5962862420439144_changed_cgtftdbe7,
    ],
    event: 'changed',
  });
  const act_8358196963355853_emit_79utfffhm = new actionTypes.EmitAction({
    actionArgs: act_8358196963355853_emit_79utfffhm_getActionArgs,
    displayName: 'load',
  });
  const inst_evh_8358196963355853_click_bu6ymmtu0 =
    new actionTypes.EventHandlerImpl({
      action: act_8358196963355853_emit_79utfffhm,
      event: 'click',
      displayName: 'load',
    });
  const inst_evh_8358196963355853_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8358196963355853_click_bu6ymmtu0],
    event: 'click',
  });
  const act_6424600534600827_showModal_u9w69quwz =
    new actionTypes.ShowModalAction({
      actionArgs: act_6424600534600827_showModal_u9w69quwz_getActionArgs,
    });
  const inst_evh_6424600534600827_clickPrevent_bejncke2a =
    new actionTypes.EventHandlerImpl({
      action: act_6424600534600827_showModal_u9w69quwz,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_6424600534600827_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6424600534600827_clickPrevent_bejncke2a],
      event: 'click.prevent',
    });
  const act_8249915319319009_showModal_gzp8va95p =
    new actionTypes.ShowModalAction({
      actionArgs: act_8249915319319009_showModal_gzp8va95p_getActionArgs,
    });
  const inst_evh_8249915319319009_clickPrevent_1pnq1bsbw =
    new actionTypes.EventHandlerImpl({
      action: act_8249915319319009_showModal_gzp8va95p,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_8249915319319009_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8249915319319009_clickPrevent_1pnq1bsbw],
      event: 'click.prevent',
    });
  const act_2276125547171722_showModal_evby1d8gp =
    new actionTypes.ShowModalAction({
      actionArgs: act_2276125547171722_showModal_evby1d8gp_getActionArgs,
    });
  const inst_evh_2276125547171722_clickPrevent_phoryn5vt =
    new actionTypes.EventHandlerImpl({
      action: act_2276125547171722_showModal_evby1d8gp,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_2276125547171722_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2276125547171722_clickPrevent_phoryn5vt],
      event: 'click.prevent',
    });
  const act_1836715606397834_showModal_001nso7c8 =
    new actionTypes.ShowModalAction({
      actionArgs: act_1836715606397834_showModal_001nso7c8_getActionArgs,
    });
  const inst_evh_1836715606397834_clickPrevent_srvylkv6v =
    new actionTypes.EventHandlerImpl({
      action: act_1836715606397834_showModal_001nso7c8,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_1836715606397834_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1836715606397834_clickPrevent_srvylkv6v],
      event: 'click.prevent',
    });

  const actions = {
    inst_evh_2248226175642056_input_sk80exgs3,
    load: inst_evh_2248226175642056_input_sk80exgs3,
    inst_evh_2248226175642056_input,
    inst_evh_5962862420439144_changed_kk8dqb8ai,
    inst_evh_5962862420439144_changed_cgtftdbe7,
    inst_evh_5962862420439144_changed,
    inst_evh_8358196963355853_click_bu6ymmtu0,
    inst_evh_8358196963355853_click,
    inst_evh_6424600534600827_clickPrevent_bejncke2a,
    inst_evh_6424600534600827_clickPrevent,
    inst_evh_8249915319319009_clickPrevent_1pnq1bsbw,
    inst_evh_8249915319319009_clickPrevent,
    inst_evh_2276125547171722_clickPrevent_phoryn5vt,
    inst_evh_2276125547171722_clickPrevent,
    inst_evh_1836715606397834_clickPrevent_srvylkv6v,
    inst_evh_1836715606397834_clickPrevent,
  };

  const evh_2248226175642056_input_sk80exgs3 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_input_sk80exgs3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_input = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_changed_kk8dqb8ai = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_changed_kk8dqb8ai.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_changed_cgtftdbe7 = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_changed_cgtftdbe7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_changed = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_changed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8358196963355853_click_bu6ymmtu0 = async (event, scope) => {
    return await actions.inst_evh_8358196963355853_click_bu6ymmtu0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8358196963355853_click = async (event, scope) => {
    return await actions.inst_evh_8358196963355853_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6424600534600827_clickPrevent_bejncke2a = async (event, scope) => {
    return await actions.inst_evh_6424600534600827_clickPrevent_bejncke2a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6424600534600827_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_6424600534600827_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8249915319319009_clickPrevent_1pnq1bsbw = async (event, scope) => {
    return await actions.inst_evh_8249915319319009_clickPrevent_1pnq1bsbw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8249915319319009_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_8249915319319009_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2276125547171722_clickPrevent_phoryn5vt = async (event, scope) => {
    return await actions.inst_evh_2276125547171722_clickPrevent_phoryn5vt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2276125547171722_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_2276125547171722_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1836715606397834_clickPrevent_srvylkv6v = async (event, scope) => {
    return await actions.inst_evh_1836715606397834_clickPrevent_srvylkv6v.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1836715606397834_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_1836715606397834_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox alignItems="center" :gap="2" v-if="loaded">
    <UcAccountSelector
      cacheKey="Payments"
      v-model="item.account"
      :value="item.account"
      @input="
        !($event && typeof $event.stopPropagation === 'function') &&
          (item.account =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event);
        evh_2248226175642056_input($event, {});
      "
    >
    </UcAccountSelector>
    <DateRangeSelector
      name="paymentFilters"
      :from="item.date_from"
      :to="item.date_to"
      :style="b_5962862420439144_style"
      @changed="evh_5962862420439144_changed($event, {})"
    >
    </DateRangeSelector>
    <BtnLoad
      :action="actions.load"
      @click="evh_8358196963355853_click($event, {})"
    >
    </BtnLoad>
    <Vr class="mx-2"> </Vr>
    <BDropdown
      :text="gettext('Commands')"
      variant="light"
      class="ms-auto border rounded"
    >
      <ItpButton
        :text="pgettext('Payments', 'Add deposit')"
        :icon="$config.payment.icon"
        dropdown-item
        v-if="selectedAccountType && selectedAccountType == 'cash_book'"
        @click.prevent="evh_6424600534600827_clickPrevent($event, {})"
      >
      </ItpButton>
      <ItpButton
        :text="pgettext('Payments', 'New payment')"
        :icon="$config.payment.icon"
        dropdown-item
        v-if="selectedAccountType && selectedAccountType == 'cash_book'"
        @click.prevent="evh_8249915319319009_clickPrevent($event, {})"
      >
      </ItpButton>
      <ItpButton
        :text="pgettext('Payments', 'New transaction...')"
        :icon="$config.payment.icon"
        dropdown-item
        class="me-2"
        v-if="selectedAccountType && selectedAccountType !== 'cash_book'"
        @click.prevent="evh_2276125547171722_clickPrevent($event, {})"
      >
      </ItpButton>
      <ItpButton
        :text="pgettext('Payments', 'Import Bank Statement...')"
        :icon="$config.uploadIcon"
        dropdown-item
        class="me-2"
        @click.prevent="evh_1836715606397834_clickPrevent($event, {})"
      >
      </ItpButton>
    </BDropdown>
  </ItpBox>
</template>
